import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NativeCheckbox } from '@/components/ui/NativeCheckbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { usePhotoPreview } from '@/components/PhotoPreview';

const RATING_DEVIATION_LIMIT = 2; // ± from player's current overall (effective) rating

// Build a usable <img src> from a stored photo value (base64 data URI, absolute URL,
// or a relative /api/... path served by the backend).
const getImageUrl = (photoUrl) => {
  if (!photoUrl) return null;
  if (photoUrl.startsWith('data:image/')) return photoUrl;
  if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) return photoUrl;
  return `${window.location.origin || ''}${photoUrl}`;
};

// The game endpoint may expose the player's photo under either field; prefer the thumbnail.
const getPlayerPhoto = (p) => getImageUrl(p?.profile_photo_thumbnail || p?.profile_photo_url);

// Effective rating = admin override if set, else community rating. Shown & used as ±2 reference.
const getEffRating = (p) => {
  if (typeof p?.effective_rating === 'number') return p.effective_rating;
  if (p?.rating > 0) return p.rating;
  return p?.community_rating_average || 0;
};

export default function RatePlayersPage() {
  const previewPhoto = usePhotoPreview();
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [game, setGame] = useState(null);
  const [ratingStatus, setRatingStatus] = useState(null);
  const [players, setPlayers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [gameRating, setGameRating] = useState(null);
  const [ratings, setRatings] = useState({});
  const [kudos, setKudos] = useState({});
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [ratingsToDelete, setRatingsToDelete] = useState([]); // Track ratings to delete on submit
  const [rulesAcknowledged, setRulesAcknowledged] = useState(false);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [rulesConfirmChecked, setRulesConfirmChecked] = useState(false);

  const kudosTypes = [
    { id: 'teamwork', label: 'Good Teamwork', icon: '🤝', color: 'blue' },
    { id: 'positive_energy', label: 'Positive Energy', icon: '😊', color: 'yellow' },
    { id: 'supportive', label: 'Supportive & Encouraging', icon: '💪', color: 'orange' },
    { id: 'great_spike', label: 'Great Spike', icon: '🏐', color: 'red' },
    { id: 'great_serve', label: 'Great Serve', icon: '💥', color: 'purple' },
    { id: 'great_pass', label: 'Great Pass', icon: '🎯', color: 'green' },
    { id: 'great_defense', label: 'Great Defense', icon: '🛡️', color: 'indigo' },
    { id: 'strong_block', label: 'Strong Block', icon: '🚫', color: 'pink' },
    { id: 'great_setting', label: 'Great Setting', icon: '🙌', color: 'teal' }
  ];

  useEffect(() => {
    loadData();
  }, [gameId]);

  const loadData = async () => {
    try {
      // Get current user
      const userResponse = await api.get('/auth/me');
      setCurrentUser(userResponse.data);

      // Get game details (use the per-game endpoint to ensure `has_prior_rating` is included)
      let gameData;
      try {
        const detailsResponse = await api.get(`/games/${gameId}/details`);
        gameData = detailsResponse.data;
      } catch (e) {
        // Fallback to listing endpoint
        const gameResponse = await api.get(`/games`);
        gameData = gameResponse.data.find(g => g.id === gameId);
      }
      if (!gameData) {
        toast.error('Game not found');
        navigate('/games');
        return;
      }
      setGame(gameData);

      // Get rating status
      const statusResponse = await api.get(`/games/${gameId}/rating-status`);
      setRatingStatus(statusResponse.data);

      // Rating participation disabled — show friendly state instead of the player list
      if (statusResponse.data.participation_disabled) {
        setLoading(false);
        return;
      }

      // Check if user has submitted ratings
      const hasSubmitted = statusResponse.data.has_submitted;
      
      // If can't rate and hasn't submitted, show error and redirect
      if (!statusResponse.data.can_rate && !hasSubmitted) {
        toast.error(statusResponse.data.reason);
        setTimeout(() => navigate('/games'), 2000);
        return;
      }

      // Get existing ratings if any (for both editable and read-only views)
      if (hasSubmitted) {
        const existingResponse = await api.get(`/games/${gameId}/my-ratings`);
        setExistingSubmission(existingResponse.data);
        
        // Pre-fill game rating
        if (existingResponse.data.game_rating !== null) {
          setGameRating(existingResponse.data.game_rating);
        }
        
        // Pre-fill ratings
        const ratingsObj = {};
        existingResponse.data.ratings.forEach(r => {
          ratingsObj[r.rated_player_id] = r.rating;
        });
        setRatings(ratingsObj);

        // Pre-fill kudos
        const kudosObj = {};
        existingResponse.data.kudos.forEach(k => {
          if (!kudosObj[k.recipient_id]) {
            kudosObj[k.recipient_id] = {};
          }
          kudosObj[k.recipient_id][k.kudos_type] = true;
        });
        setKudos(kudosObj);
      }

      // Get players (registered players who participated)
      const registeredPlayers = gameData.registered_players_info || [];
      // Exclude self and players who opted out of rating participation
      const otherPlayers = registeredPlayers.filter(p => p.user_id !== userResponse.data.id && p.can_be_rated !== false);
      
      // Debug: Log player data to check if photos are present
      console.log('Players data:', otherPlayers);
      if (otherPlayers.length > 0) {
        console.log('First player:', otherPlayers[0]);
        console.log('Has profile_photo_url?', 'profile_photo_url' in otherPlayers[0]);
      }
      
      setPlayers(otherPlayers);

      // Show rules dialog on first visit when user can rate and hasn't submitted yet
      if (statusResponse.data.can_rate && !hasSubmitted) {
        setShowRulesDialog(true);
      } else {
        setRulesAcknowledged(true); // Read-only / already submitted — no need to show rules
      }

    } catch (error) {
      toast.error('Failed to load game data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (playerId, rating) => {
    // Enforce ±2 deviation from player's current overall (effective) rating when they have one
    const player = players.find((p) => p.user_id === playerId);
    const ref = getEffRating(player);
    if (ref > 0 && ratings[playerId] !== rating) {
      const low = Math.max(1, Math.ceil(ref - RATING_DEVIATION_LIMIT));
      const high = Math.min(10, Math.floor(ref + RATING_DEVIATION_LIMIT));
      if (rating < low || rating > high) {
        toast.error(
          `Your rating for ${player?.name || 'this player'} must be within ±${RATING_DEVIATION_LIMIT} of their current overall rating (${ref.toFixed(2)}). Allowed range: ${low}–${high}.`,
          { duration: 5000 }
        );
        return; // Block selection
      }
    }

    // If clicking the same rating, clear it from frontend (no backend call yet)
    if (ratings[playerId] === rating) {
      // Remove from local ratings state
      setRatings(prev => {
        const newRatings = { ...prev };
        delete newRatings[playerId];
        return newRatings;
      });
      
      // Track this rating for deletion when user clicks Save/Update
      if (!ratingsToDelete.includes(playerId)) {
        setRatingsToDelete(prev => [...prev, playerId]);
      }
    } else {
      // Set new rating
      setRatings(prev => ({
        ...prev,
        [playerId]: rating
      }));
      
      // Remove from deletion list if it was there
      setRatingsToDelete(prev => prev.filter(id => id !== playerId));
    }
  };

  const handleKudosToggle = (playerId, kudosType) => {
    setKudos(prev => ({
      ...prev,
      [playerId]: {
        ...(prev[playerId] || {}),
        [kudosType]: !(prev[playerId]?.[kudosType] || false)
      }
    }));
  };


  const handleClearRating = async (playerId) => {
    if (!window.confirm('Are you sure you want to remove your rating for this player?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/games/${gameId}/ratings/${playerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Remove rating from local state
        setRatings(prev => {
          const newRatings = { ...prev };
          delete newRatings[playerId];
          return newRatings;
        });
        
        // Remove kudos for this player too
        setKudos(prev => {
          const newKudos = { ...prev };
          delete newKudos[playerId];
          return newKudos;
        });
        
        toast.success('Rating removed successfully');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to remove rating');
      }
    } catch (error) {
      console.error('Error removing rating:', error);
      toast.error('Failed to remove rating');
    }
  };

  const handleSubmit = async () => {
    // Validation - at least one rating is recommended but not required
    if (Object.keys(ratings).length === 0 && ratingsToDelete.length === 0) {
      toast.error('Please rate at least one player');
      return;
    }

    // Convert to API format (only submit rated players)
    const ratingsArray = Object.entries(ratings).map(([rated_player_id, rating]) => ({
      rated_player_id,
      rating: parseInt(rating)
    }));

    const kudosArray = [];
    Object.entries(kudos).forEach(([recipient_id, kudosTypes]) => {
      Object.entries(kudosTypes).forEach(([kudos_type, isSelected]) => {
        if (isSelected) {
          kudosArray.push({ recipient_id, kudos_type });
        }
      });
    });

    setSubmitting(true);
    try {
      // Step 1: Delete cleared ratings
      if (ratingsToDelete.length > 0) {
        for (const playerId of ratingsToDelete) {
          try {
            await api.delete(`/games/${gameId}/ratings/${playerId}`);
          } catch (error) {
            // If rating doesn't exist, that's okay
            console.log(`Rating for player ${playerId} doesn't exist or already deleted`);
          }
        }
      }
      
      // Step 2: Submit/update remaining ratings
      await api.post(`/games/${gameId}/submit-ratings`, {
        game_rating: gameRating !== null ? parseInt(gameRating) : null,
        ratings: ratingsArray,
        kudos: kudosArray
      });

      toast.success(existingSubmission ? 'Ratings updated successfully!' : 'Ratings submitted successfully!');
      
      // Clear deletion tracking
      setRatingsToDelete([]);
      
      setTimeout(() => navigate('/games'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ratings');
    } finally {
      setSubmitting(false);
    }
  };

  const getTimeRemaining = () => {
    if (!ratingStatus?.rating_deadline) return null;
    
    const deadline = new Date(ratingStatus.rating_deadline);
    const now = new Date();
    const hoursRemaining = Math.max(0, (deadline - now) / (1000 * 60 * 60));
    
    if (hoursRemaining < 1) {
      const minutesRemaining = Math.floor(hoursRemaining * 60);
      return `${minutesRemaining} minutes remaining`;
    }
    
    return `${hoursRemaining.toFixed(1)} hours remaining`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-xl text-gray-600">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (ratingStatus?.participation_disabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Card className="shadow-lg" data-testid="rating-participation-disabled-card">
            <CardContent className="p-8 text-center space-y-4">
              <div className="text-5xl">🔒</div>
              <h2 className="text-xl font-bold text-gray-900">Rating participation is disabled</h2>
              <p className="text-gray-600">
                You have opted out of the rating system, so you can't rate players and other players can't rate you.
                Your existing ratings and kudos are kept privately. You can re-enable rating participation in Settings.
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <Button variant="outline" onClick={() => navigate('/games')} data-testid="back-to-games-btn">Back to Games</Button>
                <Button onClick={() => navigate('/settings')} className="bg-orange-600 hover:bg-orange-700" data-testid="go-to-settings-btn">Open Settings</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <Navbar />

      {/* Rating Rules Dialog */}
      <Dialog
        open={showRulesDialog}
        onOpenChange={(v) => {
          if (!v && !rulesAcknowledged) {
            // Prevent closing without agreeing
            return;
          }
          setShowRulesDialog(v);
        }}
      >
        <DialogContent
          className="max-w-sm sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-5 sm:p-6"
          data-testid="rating-rules-dialog"
          onPointerDownOutside={(e) => !rulesAcknowledged && e.preventDefault()}
          onEscapeKeyDown={(e) => !rulesAcknowledged && e.preventDefault()}
        >
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-center text-lg sm:text-xl leading-tight">
              🏐 NLV Player Rating Rules 🏐
            </DialogTitle>
            <DialogDescription className="text-center text-xs sm:text-sm">
              Please read before rating players.
            </DialogDescription>
          </DialogHeader>

          <ul className="mt-3 space-y-2.5 text-[13px] sm:text-sm text-gray-800 leading-snug">
            <li className="flex gap-2">
              <span className="shrink-0">1️⃣</span>
              <span>
                <span className="font-semibold">Be fair & balanced ⚖️</span>
                <span className="block text-gray-600 text-[12px] sm:text-xs">
                  No overrating or underrating — rate based on real performance.
                </span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="shrink-0">2️⃣</span>
              <span>
                <span className="font-semibold">Use each player's current overall rating as a reference 👀📊</span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="shrink-0">3️⃣</span>
              <span>
                <span className="font-semibold">Rate what you saw 🎯</span>
                <span className="block text-gray-600 text-[12px] sm:text-xs">
                  Only rate players whose performance you clearly witnessed.
                </span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="shrink-0">4️⃣</span>
              <span>
                <span className="font-semibold">Skip if unsure 🤷‍♂️</span>
                <span className="block text-gray-600 text-[12px] sm:text-xs">
                  Didn't see enough? Totally fine to skip.
                </span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="shrink-0">5️⃣</span>
              <span>
                <span className="font-semibold">Give kudos freely 🌟</span>
                <span className="block text-gray-600 text-[12px] sm:text-xs">
                  Exceptional performance? Give as many kudos as you want!
                </span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="shrink-0">6️⃣</span>
              <span>
                <span className="font-semibold">Ratings are anonymous 🔒</span>
                <span className="block text-gray-600 text-[12px] sm:text-xs">
                  Only admins can view each user's rating and may remove ratings that don't comply with these rules.
                </span>
              </span>
            </li>
          </ul>

          <p className="mt-3 text-center text-[12px] sm:text-xs font-semibold text-orange-700">
            💡 Fair ratings ⚖️ · Positive vibes ✨ · Better volleyball 🏐
          </p>

          <label className="flex items-start gap-2.5 mt-4 p-2.5 bg-gray-50 rounded-lg cursor-pointer">
            <NativeCheckbox
              checked={rulesConfirmChecked}
              onChange={() => setRulesConfirmChecked((v) => !v)}
              data-testid="rules-confirm-checkbox"
            />
            <span className="text-[13px] sm:text-sm font-medium text-gray-800 leading-snug">
              I have read and agree to follow these rating rules.
            </span>
          </label>

          <DialogFooter className="mt-4 flex flex-row gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowRulesDialog(false);
                navigate('/games');
              }}
              data-testid="rules-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              disabled={!rulesConfirmChecked}
              onClick={() => {
                setRulesAcknowledged(true);
                setShowRulesDialog(false);
              }}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="rules-proceed-btn"
            >
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {!ratingStatus?.can_rate && existingSubmission 
                  ? 'Your Submitted Ratings' 
                  : existingSubmission 
                    ? 'Edit Your Ratings' 
                    : 'Rate Players'}
              </h1>
              <p className="text-gray-600">{game?.title} • {new Date(game?.date).toLocaleDateString()}</p>
            </div>
            <div className="text-right flex items-center gap-3">
              {ratingStatus?.can_rate && (
                <Badge className="bg-orange-100 text-orange-700 text-sm px-4 py-2">
                  ⏱️ {getTimeRemaining()}
                </Badge>
              )}
              {!ratingStatus?.can_rate && existingSubmission && (
                <Badge className="bg-gray-100 text-gray-700 text-sm px-4 py-2">
                  🔒 Ratings Locked
                </Badge>
              )}
              {/* Close button for read-only view */}
              {!ratingStatus?.can_rate && existingSubmission && (
                <Button 
                  onClick={() => navigate('/games')}
                  variant="outline"
                  className="border-gray-300 hover:bg-gray-100"
                >
                  ✕ Close
                </Button>
              )}
            </div>
          </div>
          {/* Info message for read-only view */}
          {!ratingStatus?.can_rate && existingSubmission && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                ℹ️ The 1-hour edit window has passed. You're viewing your submitted ratings in read-only mode.
              </p>
            </div>
          )}
        </div>

        {/* Game Rating Section */}
        <Card className="mb-6 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300 border-2">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <span>🏐</span>
              Rate This Game Experience
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              How would you rate your overall experience in this game? (0 = Poor, 10 = Excellent)
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-11 gap-2">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => ratingStatus?.can_rate && setGameRating(value)}
                  disabled={!ratingStatus?.can_rate}
                  className={`h-14 rounded-lg font-bold text-lg transition-all ${
                    gameRating === value
                      ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white shadow-lg scale-110 border-2 border-yellow-600'
                      : !ratingStatus?.can_rate
                        ? 'bg-gray-200 text-gray-500 border border-gray-300 cursor-not-allowed'
                        : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
                  }`}
                >
                  {value}
                </button>
              ))}
            </div>
            {gameRating !== null && (
              <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                <p className="text-center font-semibold text-yellow-900">
                  You rated this game: <span className="text-2xl">{gameRating}/10</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="text-4xl">ℹ️</div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">How to Rate:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Rate players from 1-10 (10 being excellent) - Optional</li>
                  <li>• You can rate specific players, not required to rate all</li>
                  <li>• Give kudos for positive attributes (optional)</li>
                  <li>• You can edit your ratings for 1 hour after submission</li>
                  <li>• Ratings lock 12 hours after game ends</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Players List */}
        <div className="space-y-4">
          {players.map((player) => (
            <Card key={player.user_id} className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getPlayerPhoto(player) ? (
                      <img
                        src={getPlayerPhoto(player)}
                        alt={player.name}
                        loading="lazy"
                        onClick={() => previewPhoto(getPlayerPhoto(player), player.name)}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback) {
                            fallback.classList.remove('hidden');
                            fallback.classList.add('flex');
                          }
                        }}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-300 cursor-zoom-in"
                      />
                    ) : null}
                    <div className={`w-12 h-12 rounded-full bg-blue-500 text-white items-center justify-center text-xl font-bold ${getPlayerPhoto(player) ? 'hidden' : 'flex'}`}>
                      {player.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg truncate">{player.name}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {player.position_1st && (
                          <Badge className="bg-gray-200 text-gray-700 text-xs">
                            {player.position_1st}
                          </Badge>
                        )}
                        {/* Overall (effective) Rating Display - admin override if set, else community */}
                        {getEffRating(player) > 0 && (
                          <div className="inline-flex items-center gap-1.5 bg-orange-100 px-2.5 py-1 rounded-full" data-testid={`overall-rating-${player.user_id}`}>
                            <span className="text-xs font-medium text-gray-600">Rating:</span>
                            <span className="text-sm font-bold text-orange-600">
                              {getEffRating(player).toFixed(2)}
                            </span>
                            <span className="text-xs text-gray-500">/10</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {ratings[player.user_id] && (
                    <div className="text-right">
                      <div className="text-3xl font-bold text-green-600">
                        {ratings[player.user_id]}
                      </div>
                      <div className="text-xs text-gray-500">Your rating</div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Rating Stars */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rate Player (1-10) <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  {getEffRating(player) > 0 && (
                    <p className="text-xs text-gray-500 mb-2" data-testid={`deviation-hint-${player.user_id}`}>
                      Allowed range vs overall rating ({getEffRating(player).toFixed(2)}):{' '}
                      <span className="font-semibold text-gray-700">
                        {Math.max(1, Math.ceil(getEffRating(player) - RATING_DEVIATION_LIMIT))}–
                        {Math.min(10, Math.floor(getEffRating(player) + RATING_DEVIATION_LIMIT))}
                      </span>
                    </p>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => {
                      const ref = getEffRating(player);
                      const outOfRange =
                        ref > 0 &&
                        (num < Math.max(1, Math.ceil(ref - RATING_DEVIATION_LIMIT)) ||
                         num > Math.min(10, Math.floor(ref + RATING_DEVIATION_LIMIT)));
                      const disabled = !ratingStatus?.can_rate || outOfRange;
                      return (
                      <button
                        key={num}
                        onClick={() => ratingStatus?.can_rate && handleRatingChange(player.user_id, num)}
                        disabled={disabled}
                        title={outOfRange ? `Outside allowed range (±${RATING_DEVIATION_LIMIT} of current ${ref.toFixed(2)})` : undefined}
                        className={`w-12 h-12 rounded-lg font-bold text-lg transition-all ${
                          ratings[player.user_id] === num
                            ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg scale-110'
                            : disabled
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {num}
                      </button>
                      );
                    })}
                  </div>
                </div>

                {/* Kudos */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Give Kudos (optional)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {kudosTypes.map(kudosType => {
                      const isSelected = kudos[player.user_id]?.[kudosType.id];
                      return (
                        <div
                          key={kudosType.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all relative ${
                            ratingStatus?.can_rate ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                          } ${
                            isSelected
                              ? 'border-orange-500 bg-orange-100 shadow-md'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                          onClick={() => ratingStatus?.can_rate && handleKudosToggle(player.user_id, kudosType.id)}
                        >
                          {/* Selected indicator */}
                          {isSelected && (
                            <div className="absolute top-1 right-1">
                              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                          <span onClick={(e) => e.stopPropagation()}>
                            <NativeCheckbox
                              checked={isSelected || false}
                              onChange={() => ratingStatus?.can_rate && handleKudosToggle(player.user_id, kudosType.id)}
                              disabled={!ratingStatus?.can_rate}
                            />
                          </span>
                          <span className="text-2xl">{kudosType.icon}</span>
                          <span className={`text-sm font-medium ${isSelected ? 'text-orange-900' : 'text-gray-700'}`}>
                            {kudosType.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Submit Button - Only show in editable mode */}
        {ratingStatus?.can_rate && (
          <div className="mt-8 flex gap-4">
            <Button
              onClick={() => navigate('/games')}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(ratings).length === 0}
              className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-6 text-lg"
            >
              {submitting ? 'Submitting...' : existingSubmission ? 'Update Ratings' : 'Submit Ratings'}
            </Button>
          </div>
        )}

        {/* Back button for read-only mode */}
        {!ratingStatus?.can_rate && existingSubmission && (
          <div className="mt-8 flex justify-center">
            <Button
              onClick={() => navigate('/games')}
              className="px-8 py-6 text-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold"
            >
              ← Back to Games
            </Button>
          </div>
        )}

        {/* Summary */}
        <div className="mt-6 text-center text-sm text-gray-600">
          Rated {Object.keys(ratings).length} of {players.length} players •{' '}
          {Object.values(kudos).reduce((sum, playerKudos) => 
            sum + Object.values(playerKudos).filter(Boolean).length, 0
          )} kudos given
        </div>
      </div>
    </div>
  );
}
